/**
 * Captures an organization-admin invitation token out of the URL before anything
 * else in the app can observe it. Mirrors src/lib/agentInviteBootstrap.ts.
 *
 * WHY A FRAGMENT AND NOT A QUERY PARAMETER
 * The invitation token is a single-use, seven-day credential that grants the
 * platform admin role (and, for a bound invite, attaches the account to an
 * organization). A `?org_invite=` query parameter is sent to the server on the
 * initial page request, so it is recorded with Vercel's runtime request details
 * and travels in the `Referer` header to any third-party asset the page loads.
 * A fragment is processed by the browser and never leaves it. founder-invite and
 * console-resend now emit `${siteUrl}/activate#org_invite=<token>`; the older
 * `${siteUrl}/#org_invite=` form is still honoured.
 *
 * LEGACY LINKS
 * Invitations emailed before this change carry `?org_invite=`. They are still
 * honoured — the credential is live for seven days — and scrubbed the same way;
 * nothing can be done about their initial request having already reached the
 * host, which is exactly why new links use the fragment.
 *
 * WHY THIS MODULE SELF-EXECUTES ON IMPORT
 * ES module side effects run in import order and before any statement in the
 * importing module, so importing this file SECOND in src/main.tsx (right after
 * the agent bootstrap, before @sentry/react and before App) guarantees the URL
 * is scrubbed before `Sentry.init()` runs, before `<Analytics />` can mount, and
 * before any other module reads `window.location`. orgInviteBootstrap.test.ts
 * pins that import order.
 *
 * WHERE THE TOKEN LIVES
 * In `capturedToken` below — module-scoped browser memory, and nowhere else. It
 * is never written to a query parameter, web storage, a cookie, `history.state`,
 * the console, Sentry context, or analytics. A refresh therefore loses it; the
 * invitee reopens their email, which is the correct trade for a bearer credential.
 */

/** org_admin_invites.token is `uuid NOT NULL DEFAULT gen_random_uuid()`. */
const TOKEN_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The only routes the emailed link points at, and the only routes this acts on.
 * "/" is the original homepage landing; "/activate" is the Slice 5 pre-auth
 * preview that new links target. Exact match only — no prefix matching.
 */
const LANDING_PATHS: ReadonlySet<string> = new Set(["/", "/activate"]);

const FRAGMENT_PREFIX = "#org_invite=";
const LEGACY_QUERY_KEY = "org_invite";

/** Module-scoped browser memory. Never persisted, never serialised. */
let capturedToken: string | null = null;

/** The minimum surface this needs, so it can be exercised without a DOM. */
export interface BootstrapWindow {
  location: { pathname: string; hash: string; search: string };
  history: { replaceState(state: unknown, unused: string, url: string): void };
}

export type CaptureOutcome =
  /** A well-formed token was taken into memory and the URL was cleaned. */
  | "captured"
  /** An invite-shaped value was present but unusable; the URL was still cleaned. */
  | "scrubbed"
  /** Nothing to do — not the landing route, or no invite in the URL at all. */
  | "noop";

/** Pure token check: exactly a uuid, nothing else. */
export function parseOrgInviteToken(value: string | null | undefined): string | null {
  if (!value) return null;
  return TOKEN_PATTERN.test(value) ? value : null;
}

/**
 * Reads the fragment (preferred) or a legacy `?org_invite=` query (honoured for
 * links emailed before the fragment change), keeps a valid token in memory, and
 * rewrites the URL without either carrier. Unrelated query parameters and an
 * unrelated hash are preserved. Passes `null` as the history state so the token
 * cannot end up in `history.state`.
 *
 * Safe during SSR and prerendering: with no `window`, it does nothing.
 */
export function captureOrgInviteFromUrl(
  win: BootstrapWindow | undefined = typeof window === "undefined"
    ? undefined
    : (window as unknown as BootstrapWindow),
): CaptureOutcome {
  if (!win) return "noop";

  // Trailing slashes are added by link rewriters, mail-security proxies and
  // humans retyping a URL, and they can repeat ("/activate//"). React Router
  // still renders every one of those as the landing page, so ALL of them are
  // collapsed — stripping only one would leave "/activate/" outside the
  // landing set and the credential in the bar for Sentry and analytics to
  // observe. "/" itself (length 1) is left alone, and a path that is nothing
  // but slashes falls back to "/".
  const path = win.location.pathname;
  const landing = path.length > 1 ? path.replace(/\/+$/, "") || "/" : path;
  if (!LANDING_PATHS.has(landing)) return "noop";

  const hasFragment = win.location.hash.startsWith(FRAGMENT_PREFIX);
  const params = new URLSearchParams(win.location.search);
  const hasLegacy = params.has(LEGACY_QUERY_KEY);
  if (!hasFragment && !hasLegacy) return "noop";

  const fromFragment = hasFragment
    ? parseOrgInviteToken(win.location.hash.slice(FRAGMENT_PREFIX.length))
    : null;
  const fromLegacy = hasLegacy ? parseOrgInviteToken(params.get(LEGACY_QUERY_KEY)) : null;
  const token = fromFragment ?? fromLegacy;
  if (token) capturedToken = token;

  params.delete(LEGACY_QUERY_KEY);
  const query = params.toString();
  const hash = hasFragment ? "" : win.location.hash;
  win.history.replaceState(
    null,
    "",
    `${landing}${query ? `?${query}` : ""}${hash}`,
  );

  return token ? "captured" : "scrubbed";
}

/** What AuthModal / LandingPage / ActivatePage read. Null once spent, or after a refresh. */
export function readCapturedOrgInvite(): string | null {
  return capturedToken;
}

/** Drops the credential from memory once redemption has succeeded. */
export function clearCapturedOrgInvite(): void {
  capturedToken = null;
}

/** Test seam. Not used by application code. */
export function __setCapturedOrgInviteForTests(token: string | null): void {
  capturedToken = token;
}

// The self-execution described in the header. Must stay the last statement, and
// this module must stay the second import in src/main.tsx (after the agent one).
captureOrgInviteFromUrl();
